import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { parseChapterSections } from "@/zapi-frontend/shared/lib/contentBlocks";
import type { TChapterSection, TContentBlock } from "@/zapi-frontend/shared/types/contentBlocks";
import { ProseBlock } from "./_ProseBlock";
import { TableBlock } from "./_TableBlock";
import { HeadlineBlock } from "./_HeadlineBlock";
import { PullQuoteBlock } from "./_PullQuoteBlock";
import { BarRowsBlock } from "./_BarRowsBlock";
import { StackedShareBlock } from "./_StackedShareBlock";
import { SeriesBlock } from "./_SeriesBlock";
import { ChainBlock } from "./_ChainBlock";
import { memoType } from "./memoTypography";

// Exported so the RQASim twin draws its body through the same components a chapter does: a
// second render layer would be every widget kind drawn twice, diverging on the first change.
//
// A block's kind says what it holds; its `render` suggestion says how it is drawn. Nothing here
// reads the data to pick a form, so switching a table to a chart and back shows the same rows.
export const renderBlock = (block: TContentBlock, index: number, siblings: TContentBlock[] = []) => {
  switch (block.kind) {
    case "prose":
      return <ProseBlock key={index} block={block} />;
    case "headline":
      return <HeadlineBlock key={index} block={block} />;
    case "pullQuote":
      return <PullQuoteBlock key={index} block={block} blocks={siblings} />;
    case "chain":
      return <ChainBlock key={index} block={block} />;
    case "table":
      switch (block.render?.kind) {
        case "barRows":
          return <BarRowsBlock key={index} block={block} render={block.render} />;
        case "stackedShare":
          return <StackedShareBlock key={index} block={block} render={block.render} />;
        case "series":
          return <SeriesBlock key={index} block={block} render={block.render} />;
        default:
          return <TableBlock key={index} block={block} />;
      }
  }
};

// The design runs paragraphs 12 px apart and gives every widget 16 px of air on both sides.
const blockGapClass = (blocks: TContentBlock[], index: number) => {
  const isProse = blocks[index].kind === "prose";
  if (index === 0) return isProse ? "mt-3" : "mt-4";
  return isProse && blocks[index - 1].kind === "prose" ? "mt-3" : "mt-4";
};

function ChapterSection({ section }: { section: TChapterSection }) {
  return (
    <section className="flex min-w-0 flex-col" data-section-id={section.id || undefined}>
      <h2 className={memoType.sectionTitle}>{section.title}</h2>
      {section.intent && <p className={cn("mt-2", memoType.sectionIntent)}>{section.intent}</p>}
      {section.blocks.map((block, index) => (
        <div key={index} className={cn("min-w-0", blockGapClass(section.blocks, index))}>
          {renderBlock(block, index, section.blocks)}
        </div>
      ))}
    </section>
  );
}

type TChapterBlocksProps = {
  content: string;
  title?: string | null;
  className?: string;
};

export function ChapterBlocks({ content, title, className }: TChapterBlocksProps) {
  const { t } = useTranslation("projectHome");
  const sections = useMemo(() => parseChapterSections(content), [content]);

  if (sections.length === 0) {
    return (
      <div className={className}>
        <FigmaTypography variant="body-2" color="zinc500">
          {t("chapterBlocks.empty")}
        </FigmaTypography>
      </div>
    );
  }

  return (
    <div className={cn("flex min-w-0 flex-col gap-4", className)} data-testid="chapter-blocks">
      {title && <h1 className={memoType.chapterTitle}>{title}</h1>}
      {sections.map((section, index) => (
        <Fragment key={section.id || index}>
          {index > 0 && <hr className="h-px border-0 bg-innoveraZinc-150" />}
          <ChapterSection section={section} />
        </Fragment>
      ))}
    </div>
  );
}
